import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  useMaterialReactTable,
} from "material-react-table";

import type { EventLogEntry } from "@core/apis/shared";

import {
  ChangeSide,
  formatChangedBy,
  formatEventChange,
  formatEventSource,
  formatEventTimestamp,
  formatEventType,
  formatFeedTemperature,
  getMergedBreakpoints,
  hasEventBreakpoints,
} from "./event-log.utils";

type EventsTableProps = Readonly<{
  events: EventLogEntry[];
  isLoading?: boolean;
}>;

/** Shared horizontal cell inset so the detail panel lines up under the main columns. */
const CELL_PX = 2;

/**
 * The "Source" column (the origin/type of `changed_by`) is temporarily hidden based
 * on Louise comments. The "Changed by" column stays.
 *
 * To re-enable once the backend lands: set this to `true`. Nothing else needs changing —
 * the column is still defined, and the breakpoints detail panel re-anchors its Outdoor
 * column under Source automatically (see `AlignedBreakpoints`).
 */
const SHOW_SOURCE_COLUMN = false;

/** Fixed column widths — required so the detail panel can align with the main table. */
const COLUMN_SIZES = {
  timestamp: 180,
  type: 200,
  changedBy: 160,
  source: 160,
  changedFrom: 200,
  changedTo: 200,
} as const;

/**
 * The breakpoints detail panel, rendered with the same column widths as the main table so
 * the Feed-from / Feed-to columns line up under Changed from / Changed to. The Outdoor
 * column anchors to the slot immediately left of "Changed from" — the Source column when
 * it's visible, otherwise "Changed by" (Source is temporarily hidden; see
 * SHOW_SOURCE_COLUMN). This keeps the panel aligned regardless of Source's visibility.
 */
function AlignedBreakpoints({
  row,
  table,
}: Readonly<{ row: MRT_Row<EventLogEntry>; table: MRT_TableInstance<EventLogEntry> }>) {
  const { t } = useTranslation(["propai_properties", "_common"]);
  const theme = useTheme();

  const breakpoints = getMergedBreakpoints(row.original);
  if (breakpoints.length === 0) return null;

  const sizeOf = (id: string) => table.getColumn(id).getSize();
  // Visible columns that precede "Changed from"; the last one hosts the Outdoor column.
  const leadIds = ["timestamp", "type", "changedBy", "source"].filter((id) =>
    table.getColumn(id)?.getIsVisible()
  );
  const outdoorId = leadIds.pop() ?? "changedBy";
  const leadWidth = leadIds.reduce((sum, id) => sum + sizeOf(id), 0);
  const outdoorWidth = sizeOf(outdoorId);
  const fromWidth = sizeOf("changedFrom");
  const toWidth = sizeOf("changedTo");

  const headCellSx = { ...theme.typography.labelMedium, px: CELL_PX, border: 0 } as const;
  const bodyCellSx = { ...theme.typography.bodyMedium, px: CELL_PX, border: 0 } as const;

  return (
    <Box sx={{ pl: `${leadWidth}px`, py: 1 }}>
      <Table
        size="small"
        sx={{ tableLayout: "fixed", width: outdoorWidth + fromWidth + toWidth }}
        aria-label={t("events.column_changed_to")}
      >
        <colgroup>
          <col style={{ width: outdoorWidth }} />
          <col style={{ width: fromWidth }} />
          <col style={{ width: toWidth }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>{t("events.breakpoint_outdoor_temp")}</TableCell>
            <TableCell sx={headCellSx}>{t("events.breakpoint_feed_temp_from")}</TableCell>
            <TableCell sx={headCellSx}>{t("events.breakpoint_feed_temp_to")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {breakpoints.map(({ outdoor, feedFrom, feedTo }) => (
            <TableRow key={outdoor}>
              <TableCell sx={bodyCellSx}>{outdoor}</TableCell>
              <TableCell sx={bodyCellSx}>{formatFeedTemperature(feedFrom)}</TableCell>
              <TableCell sx={bodyCellSx}>{formatFeedTemperature(feedTo)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export function EventsTable({ events, isLoading = false }: EventsTableProps) {
  const { t } = useTranslation(["propai_properties", "_common"]);
  const theme = useTheme();

  const hasExpandableRows = useMemo(
    () => events.some((event) => hasEventBreakpoints(event)),
    [events]
  );

  const columns = useMemo<MRT_ColumnDef<EventLogEntry>[]>(() => {
    // Radiator-curve rows show an expand toggle instead of a change value.
    const renderChangeCell = (side: ChangeSide) =>
      function ChangeCell({ row }: { row: MRT_Row<EventLogEntry> }) {
        if (hasEventBreakpoints(row.original)) {
          return (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => row.toggleExpanded()}
              sx={{ ...theme.typography.bodyMedium, color: theme.palette.primary.main }}
            >
              {t("events.expand")}
            </Link>
          );
        }
        return formatEventChange(t, row.original, side);
      };

    return [
      {
        header: t("events.column_timestamp"),
        accessorKey: "timestamp",
        size: COLUMN_SIZES.timestamp,
        grow: false,
        Cell: ({ row }) => formatEventTimestamp(row.original.timestamp),
        sortingFn: (rowA, rowB) =>
          (Date.parse(rowA.original.timestamp) || 0) - (Date.parse(rowB.original.timestamp) || 0),
      },
      {
        header: t("events.column_type"),
        id: "type",
        size: COLUMN_SIZES.type,
        grow: false,
        accessorFn: (row) => formatEventType(t, row.type),
      },
      {
        header: t("events.column_changed_by"),
        id: "changedBy",
        size: COLUMN_SIZES.changedBy,
        grow: false,
        accessorFn: (row) => formatChangedBy(t, row),
      },
      {
        header: t("events.column_source"),
        id: "source",
        size: COLUMN_SIZES.source,
        grow: false,
        accessorFn: (row) => formatEventSource(t, row),
      },
      {
        header: t("events.column_changed_from"),
        id: "changedFrom",
        size: COLUMN_SIZES.changedFrom,
        grow: false,
        enableSorting: false,
        accessorFn: (row) => formatEventChange(t, row, "from"),
        Cell: renderChangeCell("from"),
      },
      {
        header: t("events.column_changed_to"),
        id: "changedTo",
        size: COLUMN_SIZES.changedTo,
        // Fills the remaining card width while keeping the detail panel columns aligned.
        grow: true,
        enableSorting: false,
        accessorFn: (row) => formatEventChange(t, row, "to"),
        Cell: renderChangeCell("to"),
      },
    ];
  }, [t, theme]);

  const table = useMaterialReactTable<EventLogEntry>({
    columns,
    data: events,
    state: { isLoading },
    getRowId: (row, index) => row.event_uid || `event-${index}`,
    layoutMode: "grid",
    enableExpandAll: false,
    enableFilters: false,
    enableGlobalFilter: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableFullScreenToggle: false,
    enableSorting: true,
    initialState: {
      sorting: [{ id: "timestamp", desc: true }],
      pagination: { pageSize: 25, pageIndex: 0 },
      // Source hidden pending the backend `changed_by` contract.
      columnVisibility: { source: SHOW_SOURCE_COLUMN },
    },
    // Add the detail panel only when visible rows have breakpoints.
    // This avoids rendering an empty hidden expand column for other views.
    ...(hasExpandableRows
      ? {
          renderDetailPanel: ({ row, table: tableInstance }) => (
            <AlignedBreakpoints row={row} table={tableInstance} />
          ),
          // Breakpoints expand from the change cells, so hide MRT's expand column.
          displayColumnDefOptions: {
            "mrt-row-expand": {
              size: 0,
              muiTableHeadCellProps: { sx: { display: "none" } },
              muiTableBodyCellProps: { sx: { display: "none" } },
            },
          },
          muiDetailPanelProps: { sx: { p: 0, backgroundColor: theme.palette.background.paper } },
        }
      : {}),
    muiTablePaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: "none",
        borderRadius: 2,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderBottomColor: theme.palette.divider,
        px: CELL_PX,
        ...theme.typography.labelLarge,
        "& .MuiTableSortLabel-root, & .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Wrapper, & .Mui-TableHeadCell-Content-Labels":
          {
            ...theme.typography.labelLarge,
          },
        "& .MuiTableSortLabel-root": {
          color: theme.palette.text.primary,
          "&.Mui-active": {
            color: theme.palette.primary.main,
          },
        },
        "& .MuiTableSortLabel-icon": {
          color: `${theme.palette.primary.main} !important`,
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        borderBottomColor: theme.palette.divider,
        px: CELL_PX,
        ...theme.typography.bodyMedium,
      },
    },
    muiTopToolbarProps: {
      sx: { backgroundColor: theme.palette.background.paper },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        ...theme.typography.labelMedium,
      },
    },
    muiPaginationProps: {
      sx: {
        ...theme.typography.labelMedium,
        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-input, & .MuiTablePagination-actions, & .MuiSelect-select, & .MuiInputBase-input, & .MuiMenuItem-root, & .MuiPaginationItem-root, & .MuiTypography-root":
          {
            ...theme.typography.labelMedium,
          },
      },
    },
  });

  if (!isLoading && events.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 1 }}>
        {t("events.no_events")}
      </Alert>
    );
  }

  return (
    <Box
      sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, overflow: "hidden" }}
    >
      <MaterialReactTable table={table} />
    </Box>
  );
}

export default EventsTable;
